// src/utils/timestampUtils.ts
// Timestamp utility functions for CST timezone handling and validation

const CST_TIMEZONE = 'America/Chicago';

/**
 * Validates if a timestamp string is valid
 */
export function isValidTimestamp(ts: string | null | undefined): boolean {
  if (!ts || typeof ts !== 'string') return false;
  
  const date = new Date(ts);
  return !Number.isNaN(date.getTime());
}

/**
 * Checks if timestamp is in the future (with optional buffer)
 */
export function isFutureTimestamp(ts: string, bufferMinutes: number = 5): boolean {
  if (!isValidTimestamp(ts)) return false;
  
  const date = new Date(ts);
  const now = new Date();
  const bufferMs = bufferMinutes * 60 * 1000;
  
  return date.getTime() > (now.getTime() + bufferMs);
}

/**
 * Safely parses a timestamp string to Date object
 * Assumes UTC if no timezone is specified
 */
export function parseTimestamp(ts: string | null | undefined): Date | null {
  if (!ts) return null;
  
  if (!isValidTimestamp(ts)) {
    console.warn('Invalid timestamp:', ts);
    return null;
  }
  
  try {
    // Parse the timestamp - JavaScript Date handles ISO strings with or without timezone
    // If no timezone is specified, Date assumes local timezone, but we want to treat it as UTC
    let timestampStr = ts;
    
    // Check if timestamp has timezone info
    const hasTimezone = ts.includes('Z') || ts.includes('+') || /-\d{2}:\d{2}$/.test(ts);
    
    if (!hasTimezone) {
      // No timezone indicator - check if it ends with just digits (might be missing timezone)
      // Assume it's UTC if no timezone is present
      if (!ts.endsWith('Z')) {
        timestampStr = `${ts}Z`; // Append Z to indicate UTC
      }
    }
    
    const date = new Date(timestampStr);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing timestamp:', ts, error);
    return null;
  }
}

/**
 * Converts a Date to CST timezone
 */
export function toCST(date: Date): Date {
  // Use Intl API to format in CST timezone
  // This automatically handles CST/CDT transitions
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: CST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(date);
  const year = Number.parseInt(parts.find(p => p.type === 'year')?.value || '0');
  const month = Number.parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1;
  const day = Number.parseInt(parts.find(p => p.type === 'day')?.value || '0');
  const hour = Number.parseInt(parts.find(p => p.type === 'hour')?.value || '0');
  const minute = Number.parseInt(parts.find(p => p.type === 'minute')?.value || '0');
  const second = Number.parseInt(parts.find(p => p.type === 'second')?.value || '0');
  
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Formats timestamp for chat messages (smart relative formatting)
 * Returns formatted string in CST timezone
 */
export function formatTimestampForChat(ts: string | null | undefined): string {
  const date = parseTimestamp(ts);
  if (!date) return ts || 'Invalid date';
  
  const now = new Date();
  
  // Get date strings in CST for comparison
  const dateStrCST = date.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  const nowStrCST = now.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  
  // Calculate difference in days (using UTC dates but comparing CST dates)
  const datePartsCST = dateStrCST.split('/');
  const nowPartsCST = nowStrCST.split('/');
  const dateCST = new Date(Number.parseInt(datePartsCST[2]), Number.parseInt(datePartsCST[0]) - 1, Number.parseInt(datePartsCST[1]));
  const nowCST = new Date(Number.parseInt(nowPartsCST[2]), Number.parseInt(nowPartsCST[0]) - 1, Number.parseInt(nowPartsCST[1]));
  const diffDays = Math.floor((nowCST.getTime() - dateCST.getTime()) / (1000 * 60 * 60 * 24));
  
  // Format time in CST
  const timeStr = date.toLocaleTimeString('en-US', {
    timeZone: CST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  
  if (diffDays === 0) {
    // Today - show only time
    return timeStr;
  } else if (diffDays === 1) {
    // Yesterday
    return `Yesterday, ${timeStr}`;
  } else if (diffDays < 7) {
    // This week - show day name
    const dayName = date.toLocaleDateString('en-US', {
      timeZone: CST_TIMEZONE,
      weekday: 'short',
    });
    return `${dayName}, ${timeStr}`;
  } else {
    // Older - show date and time
    const datePart = date.toLocaleDateString('en-US', {
      timeZone: CST_TIMEZONE,
      month: 'short',
      day: 'numeric',
    });
    return `${datePart}, ${timeStr}`;
  }
}

/**
 * Formats full timestamp with date and time in CST
 */
export function formatFullTimestamp(ts: string | null | undefined): string {
  const date = parseTimestamp(ts);
  if (!date) return ts || 'Invalid date';
  
  const cstDate = new Date(date.toLocaleString('en-US', { timeZone: CST_TIMEZONE }));
  
  return cstDate.toLocaleString('en-US', {
    timeZone: CST_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Formats timestamp for chat history list (date + time in CST)
 * Always shows full date and time: "Jan 15, 2:30 PM" or "Feb 5, 2:30 PM"
 */
export function formatTimestampForHistory(ts: string | null | undefined): string {
  const date = parseTimestamp(ts);
  if (!date) return ts || '';
  
  // Format date in CST (month day)
  const datePart = date.toLocaleDateString('en-US', {
    timeZone: CST_TIMEZONE,
    month: 'short',
    day: 'numeric',
  });
  
  // Format time in CST
  const timeStr = date.toLocaleTimeString('en-US', {
    timeZone: CST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  
  // Always return date + time
  return `${datePart}, ${timeStr}`;
}

/**
 * Formats timestamp for calculation history (full date + time with seconds in CST)
 * Format: "M/D/YYYY, H:MM:SS AM/PM" (e.g., "2/19/2026, 5:12:02 PM")
 * Uses EXACTLY the same logic as formatTimestampForChat to ensure perfect consistency
 */
export function formatTimestampForCalculationHistory(ts: string | null | undefined): string {
  // Use EXACTLY the same parsing and formatting logic as formatTimestampForChat
  const date = parseTimestamp(ts);
  if (!date) return ts || 'Invalid date';
  
  // Use the exact same toLocaleTimeString call as formatTimestampForChat, just add seconds
  // This ensures identical timezone conversion behavior
  const timeStr = date.toLocaleTimeString('en-US', {
    timeZone: CST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit', // Only difference: include seconds
    hour12: true,
  });
  
  // Format date in CST (M/D/YYYY) - same method as formatTimestampForChat
  const datePart = date.toLocaleDateString('en-US', {
    timeZone: CST_TIMEZONE,
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  
  // Return formatted string: "M/D/YYYY, H:MM:SS AM/PM"
  return `${datePart}, ${timeStr}`;
}

/**
 * Formats date separator for chat (Today, Yesterday, or date)
 */
export function formatDateSeparator(ts: string | null | undefined): string {
  const date = parseTimestamp(ts);
  if (!date) return '';
  
  const now = new Date();
  
  // Get date strings in CST for comparison
  const dateStrCST = date.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  const nowStrCST = now.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  
  // Calculate difference in days
  const datePartsCST = dateStrCST.split('/');
  const nowPartsCST = nowStrCST.split('/');
  const dateCST = new Date(Number.parseInt(datePartsCST[2]), Number.parseInt(datePartsCST[0]) - 1, Number.parseInt(datePartsCST[1]));
  const nowCST = new Date(Number.parseInt(nowPartsCST[2]), Number.parseInt(nowPartsCST[0]) - 1, Number.parseInt(nowPartsCST[1]));
  const diffDays = Math.floor((nowCST.getTime() - dateCST.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      timeZone: CST_TIMEZONE,
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
}

/**
 * Checks if two timestamps are on different days (in CST)
 */
export function isDifferentDay(ts1: string | null | undefined, ts2: string | null | undefined): boolean {
  const date1 = parseTimestamp(ts1);
  const date2 = parseTimestamp(ts2);
  
  if (!date1 || !date2) return false;
  
  // Get date strings in CST for comparison
  const date1StrCST = date1.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  const date2StrCST = date2.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE });
  
  return date1StrCST !== date2StrCST;
}

/**
 * Gets user's timezone (for reference, though we use CST)
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return CST_TIMEZONE;
  }
}

